import type { WrappedSession } from '../sip/ua';

export type MultiLegState =
  | { kind: 'idle' }
  | { kind: 'primary-active'; primary: WrappedSession; primaryCallId: string }
  | { kind: 'consulting'; primary: WrappedSession; primaryCallId: string; consult: WrappedSession; consultCallId: string; consultTarget: string }
  | { kind: 'transferring'; primary: WrappedSession; primaryCallId: string; consult: WrappedSession; consultCallId: string; consultTarget: string }
  | { kind: 'conferencing'; primary: WrappedSession; primaryCallId: string; consult: WrappedSession; consultCallId: string; consultTarget: string }
  | { kind: 'error'; reason: string };

export interface MultiLegOrchestratorDeps {
  /** Place a new outbound leg and return the wrapped session. */
  placeCall: (target: string) => WrappedSession;
  /** Emitted on every state transition. */
  onStateChange: (state: MultiLegState) => void;
  /**
   * F4.2-acceptance HF4: optional REFER replaces injection for tests that want
   * to control the SIP wire path explicitly. Production code can omit this and
   * the orchestrator will fall back to `primary.referReplaces(consult)` on the
   * WrappedSession itself, which uses JsSIP's native replaces API (cleaner +
   * no manual dialog-tag extraction).
   */
  referReplaces?: (primary: WrappedSession, consult: WrappedSession) => Promise<void>;
}

/**
 * State machine for multi-leg call transfer (warm/attended). Holds explicit
 * states for primary-active, consulting, transferring, and idle. All
 * transitions are synchronous except completeWarmTransfer (awaits SIP REFER).
 */
export class MultiLegOrchestrator {
  private state: MultiLegState = { kind: 'idle' };

  constructor(private readonly deps: MultiLegOrchestratorDeps) {}

  getState(): MultiLegState {
    return this.state;
  }

  setPrimary(input: { session: WrappedSession; callId: string }): void {
    this.transition({ kind: 'primary-active', primary: input.session, primaryCallId: input.callId });
  }

  startConsult(target: string): void {
    if (this.state.kind !== 'primary-active') {
      throw new Error(`startConsult requires primary-active state, got ${this.state.kind}`);
    }
    const { primary, primaryCallId } = this.state;
    primary.setHold(true);
    const consult = this.deps.placeCall(target);
    const consultCallId = consult.getCallId() ?? '';
    this.transition({ kind: 'consulting', primary, primaryCallId, consult, consultCallId, consultTarget: target });
  }

  cancelConsult(): Promise<void> {
    if (this.state.kind !== 'consulting') return Promise.resolve();
    const { primary, primaryCallId, consult } = this.state;
    consult.hangup();
    primary.setHold(false);
    this.transition({ kind: 'primary-active', primary, primaryCallId });
    return Promise.resolve();
  }

  async completeWarmTransfer(): Promise<void> {
    if (this.state.kind !== 'consulting') {
      throw new Error(`completeWarmTransfer requires consulting state, got ${this.state.kind}`);
    }
    const { primary, primaryCallId, consult, consultCallId, consultTarget } = this.state;
    this.transition({ kind: 'transferring', primary, primaryCallId, consult, consultCallId, consultTarget });
    const referReplaces = this.deps.referReplaces ?? ((p, c) => p.referReplaces(c));
    try {
      await referReplaces(primary, consult);
      this.transition({ kind: 'idle' });
    } catch (err) {
      // Roll back to consulting so the user can retry or cancel.
      this.transition({ kind: 'consulting', primary, primaryCallId, consult, consultCallId, consultTarget });
      throw err;
    }
  }

  /**
   * F4.4: returns which leg ('primary' or 'consult') corresponds to the given
   * call ID, or null if no match. Works in `consulting` and `conferencing`
   * states (both carry a `consult` leg). Used by CallSessionOrchestrator.onEnded
   * to route call-end events to the correct multi-leg transition.
   */
  whichLegByCallId(callId: string): 'primary' | 'consult' | null {
    if (
      this.state.kind === 'consulting' ||
      this.state.kind === 'conferencing' ||
      this.state.kind === 'transferring'
    ) {
      if (this.state.primaryCallId === callId && callId !== '') return 'primary';
      if (this.state.consultCallId === callId && callId !== '') return 'consult';
    }
    return null;
  }

  /**
   * F4.2-cleanup #4: legacy alias preserved for back-compat with consumers that
   * only care about the consult leg (CallSessionOrchestrator.onEnded). Delegates
   * to whichLegByCallId.
   */
  isConsultCallId(callId: string): boolean {
    return this.whichLegByCallId(callId) === 'consult';
  }

  /** Called when the consult B-leg fails (no answer / network drop / etc). */
  onConsultFailed(_reason: string): void {
    if (this.state.kind !== 'consulting') return;
    const { primary, primaryCallId } = this.state;
    primary.setHold(false);
    this.transition({ kind: 'primary-active', primary, primaryCallId });
  }

  /**
   * F4.4: convert an active consult into a 3-way conference.
   * Transitions consulting → conferencing by un-holding the primary leg.
   * The audio bridge between A and B depends on the SBC (Asterisk auto-bridge);
   * client-side this just keeps both sessions active in parallel.
   */
  addToConference(): void {
    if (this.state.kind !== 'consulting') {
      throw new Error(`addToConference requires consulting state, got ${this.state.kind}`);
    }
    const { primary, primaryCallId, consult, consultCallId, consultTarget } = this.state;
    primary.setHold(false);
    this.transition({ kind: 'conferencing', primary, primaryCallId, consult, consultCallId, consultTarget });
  }

  /**
   * F4.4: end the active 3-way conference. Hangs up both legs and returns to
   * idle. No-op if not in conferencing state (defensive).
   */
  endConference(): Promise<void> {
    if (this.state.kind !== 'conferencing') return Promise.resolve();
    const { primary, consult } = this.state;
    consult.hangup();
    primary.hangup();
    this.transition({ kind: 'idle' });
    return Promise.resolve();
  }

  /**
   * F4.4: remove one participant from the active 3-way conference. The other
   * leg becomes the active 1:1 call. If 'consult' is dropped, primary stays
   * primary. If 'primary' is dropped, the former consult becomes the new
   * primary in state (consumers in CallSessionOrchestrator should also update
   * their currentCallId/currentRemote to reflect the new primary).
   * No-op if not in conferencing state.
   */
  dropParticipant(which: 'primary' | 'consult'): Promise<void> {
    if (this.state.kind !== 'conferencing') return Promise.resolve();
    const { primary, primaryCallId, consult, consultCallId } = this.state;
    if (which === 'consult') {
      consult.hangup();
      this.transition({ kind: 'primary-active', primary, primaryCallId });
      return Promise.resolve();
    }
    primary.hangup();
    this.transition({ kind: 'primary-active', primary: consult, primaryCallId: consultCallId });
    return Promise.resolve();
  }

  reset(): void {
    if (this.state.kind === 'consulting' || this.state.kind === 'transferring') {
      try {
        this.state.consult.hangup();
      } catch {
        // ignore
      }
    }
    this.transition({ kind: 'idle' });
  }

  private transition(next: MultiLegState): void {
    this.state = next;
    this.deps.onStateChange(next);
  }
}
